/**
 * APP PROMOTION PLAYBOOK — the Google Ads App Promotion assistant's frontend
 * half. Talks only to /google-ads/app-promotion/ai/* and maps the draft onto
 * AppPromotionWizard's form state. Owned by this module; a YouTube playbook is
 * a sibling file, not an import of this one.
 */
import type { AiAssistantPlaybook, AssistantDraftRow } from '../types';
import {
  googleAdsService, AppPromotionAiApp, AppPromotionAiDraft, AppPromotionAiGoal, GoogleAdsAppBiddingGoal, GoogleAdsAppStore,
  GoogleAdsGeoSuggestion, GoogleAdsLanguage,
} from '@/services/google-ads.service';

/** Fields the assistant may fill; the wizard shows an "AI suggested" badge on each until the user edits it. */
export type AppPromotionAiField =
  | 'store' | 'appId' | 'name' | 'goal' | 'targetCpa' | 'budget' | 'startDate' | 'endDate' | 'locations' | 'languages'
  | 'headlines' | 'descriptions';

/** The slice of AppPromotionWizard state the playbook writes to. */
export interface AppPromotionAiForm {
  setStore: (v: GoogleAdsAppStore) => void;
  setAppId: (v: string) => void;
  setStorePaste: (v: string) => void;
  setName: (v: string) => void;
  setGoal: (v: GoogleAdsAppBiddingGoal) => void;
  setTargetCpa: (v: string) => void;
  setBudget: (v: string) => void;
  setStartDate: (v: string) => void;
  setEndDate: (v: string) => void;
  setLocations: (v: GoogleAdsGeoSuggestion[]) => void;
  setLanguages: (v: GoogleAdsLanguage[]) => void;
  markAi: (fields: AppPromotionAiField[]) => void;
  /** Remember the interview so the creatives step can reuse the session, goal and copy language. */
  setAiContext: (ctx: { sessionId: string; app: AppPromotionAiApp; goal?: AppPromotionAiGoal; language?: string }) => void;
}

const fromMicros = (m: number) => {
  const v = m / 1_000_000;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.?0+$/, '');
};

const GOAL_SHORT: Record<GoogleAdsAppBiddingGoal, string> = {
  OPTIMIZE_INSTALLS_TARGET_INSTALL_COST: 'App installs — target cost per install',
  OPTIMIZE_IN_APP_CONVERSIONS_TARGET_INSTALL_COST: 'Installs likely to act — target cost per install',
  OPTIMIZE_IN_APP_CONVERSIONS_TARGET_CONVERSION_COST: 'In-app actions — target cost per action',
};

/** Interview goal → the value the wizard's creatives call should pass. Inferred from the bidding goal the draft chose. */
const interviewGoalFor = (draft: AppPromotionAiDraft): AppPromotionAiGoal =>
  draft.campaign.biddingStrategyGoalType === 'OPTIMIZE_INSTALLS_TARGET_INSTALL_COST' ? 'installs' : 'in_app_purchases';

export function createAppPromotionPlaybook(opts: { accountUid: string; currency?: string }): AiAssistantPlaybook<AppPromotionAiApp, AppPromotionAiDraft, AppPromotionAiForm> {
  const cur = opts.currency ? ` ${opts.currency}` : '';
  return {
    id: 'google-ads.app-promotion',
    title: 'Start with AI',
    description: 'Paste your store listing, answer four quick questions, get a campaign draft you can edit.',
    entry: {
      label: 'Play Store or App Store URL',
      placeholder: 'https://play.google.com/store/apps/details?id=…  ·  https://apps.apple.com/…/id…',
      hint: 'The listing is read publicly — name, icon, category and screenshots. Nothing is published until you do it.',
      buttonLabel: 'Find app',
    },
    steps: ['App', 'Goal', 'Markets', 'Budget', 'Language', 'Draft'],
    applyLabel: 'Apply to form',

    async start(input) {
      const r = await googleAdsService.appPromotionAi.start(input);
      return { sessionId: r.sessionId, subject: r.app, question: r.question };
    },

    async answer(sessionId, answer) {
      return googleAdsService.appPromotionAi.answer(sessionId, answer);
    },

    async draft(sessionId) {
      return googleAdsService.appPromotionAi.draft({ sessionId, accountUid: opts.accountUid, currency: opts.currency });
    },

    describeSubject(app) {
      const badges: string[] = [app.store === 'APPLE_APP_STORE' ? 'App Store' : 'Google Play'];
      if (app.category) badges.push(app.category);
      if (app.rating != null) badges.push(`★ ${app.rating}`);
      if (app.screenshots.length) badges.push(`${app.screenshots.length} screenshots`);
      return {
        title: app.name ?? app.appId,
        subtitle: [app.developer, app.appId].filter(Boolean).join(' · '),
        imageUrl: app.iconUrl ?? undefined,
        badges,
        description: app.description ?? undefined,
      };
    },

    summarizeDraft(d): AssistantDraftRow[] {
      const c = d.campaign;
      const r = d.rationale;
      return [
        { key: 'name', label: 'Campaign name', value: c.name, rationale: r.name },
        { key: 'goal', label: 'Bidding goal', value: GOAL_SHORT[c.biddingStrategyGoalType], rationale: r.biddingStrategyGoalType },
        { key: 'cpa', label: 'Target CPA', value: `${fromMicros(c.targetCpaMicros)}${cur}`, rationale: r.targetCpaMicros },
        { key: 'budget', label: 'Daily budget', value: `${fromMicros(c.dailyBudgetMicros)}${cur}`, rationale: r.dailyBudgetMicros },
        { key: 'dates', label: 'Dates', value: `${c.startDate || 'when enabled'} → ${c.endDate ?? 'no end'}`, rationale: r.dates },
        { key: 'geo', label: 'Locations', value: d.targeting.geo.length ? d.targeting.geo.map((g) => g.name).join(', ') : 'Worldwide', rationale: r.countryCodes },
        { key: 'lang', label: 'Languages', value: d.targeting.languages.map((l) => l.name).join(', ') || 'All', rationale: r.languageCodes },
      ];
    },

    draftWarnings(d) {
      const w: string[] = [];
      if (d.targeting.unresolvedCountries.length) {
        w.push(`Could not resolve ${d.targeting.unresolvedCountries.join(', ')} to a Google location — add ${d.targeting.unresolvedCountries.length === 1 ? 'it' : 'them'} in Locations by hand.`);
      }
      const ratio = d.campaign.dailyBudgetMicros / Math.max(1, d.campaign.targetCpaMicros);
      if (ratio < 10) w.push('Daily budget is under 10× the target CPA — Google recommends at least 10× to leave learning.');
      return w;
    },

    applyDraft(d, form, ctx) {
      const c = d.campaign;
      form.setStore(c.appStore);
      form.setAppId(c.appId);
      form.setStorePaste(ctx.subject.storeUrl);
      form.setName(c.name);
      form.setGoal(c.biddingStrategyGoalType);
      form.setTargetCpa(fromMicros(c.targetCpaMicros));
      form.setBudget(fromMicros(c.dailyBudgetMicros));
      form.setStartDate(c.startDate);
      form.setEndDate(c.endDate ?? '');
      form.setLocations(d.targeting.geo.map((g) => ({ id: g.id, name: g.name, countryCode: g.countryCode, targetType: 'Country' })));
      form.setLanguages(d.targeting.languages.map((l) => ({ id: l.id, code: l.code, name: l.name, targetable: true })));
      form.markAi(['store', 'appId', 'name', 'goal', 'targetCpa', 'budget', 'startDate', 'endDate', 'locations', 'languages']);
      form.setAiContext({ sessionId: ctx.sessionId, app: ctx.subject, goal: interviewGoalFor(d), language: c.languageCodes[0] });
    },
  };
}
